/**
 * School-side reprice-at-submit helpers (409 `price_changed` / 422
 * `price_missing` off `POST /api/school-orders/carts/:id/submit`).
 *
 * The school checkout submits one server cart per `(serviceDate,
 * mealtime)` group, so — unlike the family flow's single error — a
 * bulk submit yields PER-GROUP failures. These pure helpers classify
 * each group's error and aggregate the failed groups into the one
 * PriceChangedDialog / unpriced-items message the UI shows.
 *
 * Framework-free (no React, no `@/api` runtime imports) so the api
 * layer, the pages, and the unit tests share the same logic. The wire
 * parsing itself is shared with the family flow via
 * `@/lib/orders/price-change`.
 */

import { parsePriceChanges, parseUnpricedMenuItemIds, type PriceChangeWire } from '@/lib/orders/price-change'
import { formatCents } from '@/lib/school-orders/money'

/** Typed failure fields for one `(serviceDate, mealtime)` group submit. */
export interface GroupSubmitFailure {
  error: string
  /** Present iff the group failed 409 `price_changed` with parseable changes. */
  priceChanges?: PriceChangeWire[]
  /** Present iff the group failed 422 `price_missing`. */
  unpricedMenuItemIds?: string[]
  /**
   * Present iff the group failed 400 `menu_item_not_available_on_date` —
   * stale cart lines whose menu item is no longer served on the group's
   * date (the menu changed after the cart was filled, e.g. a field-trip
   * plan blocked the day). The checkout prunes exactly these lines.
   */
  unavailableMenuItemIds?: string[]
  /** Present iff the group failed 402 `insufficient_funds` — drives the "Add funds" link. */
  insufficientFunds?: boolean
  /**
   * Present iff the group failed 409 `production_records_overdue` — the
   * campus has too many past service days with unfinished delivery
   * confirmations / production-record sign-offs, so manual ordering is
   * paused. Carries the outstanding yyyy-mm-dd dates; the UI links to
   * the Production Records page to work the backlog down.
   */
  productionRecordsOverdue?: { incompleteDays: string[] }
}

/** Parse the `{ incompleteDays: string[] }` body of a production_records_overdue 409. */
export function parseIncompleteDays(body: unknown): string[] {
  if (body && typeof body === 'object' && Array.isArray((body as { incompleteDays?: unknown }).incompleteDays)) {
    return (body as { incompleteDays: unknown[] }).incompleteDays.filter((d): d is string => typeof d === 'string')
  }
  return []
}

/**
 * Human message for the 402 `insufficient_funds` failure. Names the
 * amounts when the body carries them; falls back to a generic line so
 * the raw ledger message ("Account cxy… has 0 USD balance…") never
 * reaches the screen.
 */
export function insufficientFundsMessage(body: unknown): string {
  if (body && typeof body === 'object') {
    const { requiredCents, balanceCents, currency } = body as Partial<{
      requiredCents: number
      balanceCents: number
      currency: string
    }>
    if (typeof requiredCents === 'number' && typeof balanceCents === 'number') {
      return `Insufficient funds — this order needs ${formatCents(requiredCents, currency)}, but the account balance is ${formatCents(balanceCents, currency)}.`
    }
  }
  return 'Insufficient funds — the account balance does not cover this order.'
}

/**
 * Classify one failed group submit off the ApiError's parts. Unknown
 * codes (and a 409 whose body carries no parseable changes) fall back
 * to the plain message so the generic failure box still renders.
 */
export function classifyGroupSubmitError(
  status: number,
  code: string | null,
  body: unknown,
  message: string,
): GroupSubmitFailure {
  if (code === 'production_records_overdue') {
    return { error: message, productionRecordsOverdue: { incompleteDays: parseIncompleteDays(body) } }
  }
  if (status === 409 && code === 'price_changed') {
    const priceChanges = parsePriceChanges(body)
    if (priceChanges.length > 0) {
      return { error: message, priceChanges }
    }
  } else if (status === 422 && code === 'price_missing') {
    return { error: message, unpricedMenuItemIds: parseUnpricedMenuItemIds(body) }
  } else if (status === 400 && code === 'menu_item_not_available_on_date') {
    // Same `{ menuItemIds }` wire shape as price_missing.
    return { error: message, unavailableMenuItemIds: parseUnpricedMenuItemIds(body) }
  } else if (status === 402 && code === 'insufficient_funds') {
    return { error: insufficientFundsMessage(body), insufficientFunds: true }
  }
  return { error: message }
}

/** The result slice the aggregation helpers need (see `CheckoutGroupResult`). */
export interface GroupResultLike {
  groupKey: string
  ok: boolean
  priceChanges?: PriceChangeWire[]
  unpricedMenuItemIds?: string[]
}

/**
 * Merge the price changes across every failed group, deduped by
 * menuItemId (first occurrence wins — the fresh resolve is per
 * service date but the dialog shows one row per item).
 */
export function collectPriceChanges(results: readonly GroupResultLike[]): PriceChangeWire[] {
  const seen = new Set<string>()
  const out: PriceChangeWire[] = []
  for (const result of results) {
    if (result.ok) {
      continue
    }
    for (const change of result.priceChanges ?? []) {
      if (!seen.has(change.menuItemId)) {
        seen.add(change.menuItemId)
        out.push(change)
      }
    }
  }
  return out
}

/** Unique menuItemIds across every group that failed 422 `price_missing`. */
export function collectUnpricedMenuItemIds(results: readonly GroupResultLike[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const result of results) {
    if (result.ok) {
      continue
    }
    for (const id of result.unpricedMenuItemIds ?? []) {
      if (!seen.has(id)) {
        seen.add(id)
        out.push(id)
      }
    }
  }
  return out
}

/**
 * The `${date}::${mealtime}` keys of the groups that failed WITH price
 * changes — the exact set to resubmit with `acceptRepricing: true`
 * once the admin confirms. Groups that failed for other reasons stay
 * in the generic retry path.
 */
export function priceChangedGroupKeys(results: readonly GroupResultLike[]): Set<string> {
  const keys = new Set<string>()
  for (const result of results) {
    if (!result.ok && (result.priceChanges?.length ?? 0) > 0) {
      keys.add(result.groupKey)
    }
  }
  return keys
}

/**
 * Resolve the display names for the 422 `price_missing` ids off the
 * cart lines. Unknown ids (line already removed) fall back to a
 * generic label; duplicate names are kept once.
 */
export function unpricedLineNames(
  menuItemIds: readonly string[],
  lines: readonly { menuItemId: string; name: string }[],
): string[] {
  const nameByMenuItemId = new Map<string, string>()
  for (const line of lines) {
    if (!nameByMenuItemId.has(line.menuItemId)) {
      nameByMenuItemId.set(line.menuItemId, line.name)
    }
  }
  const out: string[] = []
  for (const id of menuItemIds) {
    const name = nameByMenuItemId.get(id) ?? 'Menu item'
    if (!out.includes(name)) {
      out.push(name)
    }
  }
  return out
}

/** Human message for the 422 `price_missing` failure, naming the lines. */
export function unpricedFailureMessage(
  menuItemIds: readonly string[],
  lines: readonly { menuItemId: string; name: string }[],
): string {
  const names = unpricedLineNames(menuItemIds, lines)
  if (names.length === 0) {
    return 'Some items have no price configured for this school — remove them and try again.'
  }
  return `No price is configured for: ${names.join(', ')}. Remove ${names.length === 1 ? 'this item' : 'these items'} and try again.`
}

/**
 * Human message for the 400 `menu_item_not_available_on_date` failure —
 * the named lines went stale (the day's menu changed after they were
 * added, e.g. a field-trip plan replaced the day) and were pruned from
 * the cart, so a resubmit places the rest.
 */
export function unavailableFailureMessage(
  menuItemIds: readonly string[],
  lines: readonly { menuItemId: string; name: string }[],
): string {
  const names = unpricedLineNames(menuItemIds, lines)
  if (names.length === 0) {
    return 'Some items are no longer on the menu for this day — they were removed from your cart. Submit again to place the rest.'
  }
  return `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} no longer on the menu for this day (the menu changed after your cart was filled). ${names.length === 1 ? 'It was' : 'They were'} removed from your cart — submit again to place the rest.`
}
